import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash, ArrowUp, ArrowDown } from "@phosphor-icons/react";

export default function MarketingChoicesTab() {
  const [choices, setChoices] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketing/choices");
      setChoices(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    try {
      const { data } = await api.patch("/marketing/choices", patch);
      setChoices(data);
      toast.success("Saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  if (loading || !choices) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="marketing-choices-tab">
      <div>
        <div className="label-tracking mb-1">Marketing</div>
        <h2 className="font-display text-2xl text-slate-900">Marketing tracker choices</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">Manage the options that appear in the Add Item wizard and list filters. Existing items keep the value they were saved with, so renaming here doesn't break history.</p>
      </div>

      <ChoiceList label="Classification" testid="classifications" values={choices.classifications} onChange={(v) => save({ classifications: v })}/>
      <ChoiceList label="Provided by" testid="provided-by" values={choices.provided_by_options} onChange={(v) => save({ provided_by_options: v })}/>
      <ChoiceList label="Current status" testid="statuses" values={choices.statuses} onChange={(v) => save({ statuses: v })}/>
    </div>
  );
}

function ChoiceList({ label, testid, values, onChange }) {
  const [newVal, setNewVal] = useState("");

  const add = () => {
    const v = newVal.trim();
    if (!v || values.includes(v)) return;
    onChange([...values, v]);
    setNewVal("");
  };
  const rm = (i) => onChange(values.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const next = [...values];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const rename = (i, v) => {
    const next = [...values];
    next[i] = v;
    onChange(next);
  };

  return (
    <div className="max-w-xl" data-testid={`choice-list-${testid}`}>
      <div className="label-tracking mb-2">{label}</div>
      <div className="space-y-1.5 mb-3">
        {values.map((v, i) => (
          <div key={i} className="flex items-center gap-1">
            <Input
              data-testid={`choice-${testid}-${i}`}
              value={v}
              onChange={(e) => rename(i, e.target.value)}
              onBlur={() => onChange(values.filter(Boolean))}
              className="rounded-sm h-9 text-sm"
            />
            <Button size="icon" variant="ghost" onClick={() => move(i, -1)} className="h-9 w-9 rounded-sm shrink-0"><ArrowUp size={14}/></Button>
            <Button size="icon" variant="ghost" onClick={() => move(i, 1)} className="h-9 w-9 rounded-sm shrink-0"><ArrowDown size={14}/></Button>
            <Button size="icon" variant="ghost" onClick={() => rm(i)} className="h-9 w-9 rounded-sm text-rose-600 hover:bg-rose-50 shrink-0" data-testid={`choice-${testid}-rm-${i}`}><Trash size={14}/></Button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input data-testid={`choice-${testid}-new`} value={newVal} onChange={(e) => setNewVal(e.target.value)} placeholder="Add option…" className="rounded-sm h-9 text-sm max-w-xs" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}/>
        <Button data-testid={`choice-${testid}-add`} onClick={add} size="sm" variant="outline" className="rounded-sm gap-1"><Plus size={14}/> Add</Button>
      </div>
    </div>
  );
}
